/**
 * Edpies — TileEmblem
 *
 * Decorative "poster" icon block used by feature tiles (Explore More Tools,
 * Practice Hub board cards). Wraps a Lucide icon in a 56 × 56 gradient
 * block with subtle SVG ornaments (dots / sparkles / wavy strokes) layered
 * behind it so each tile feels designed, not template-generated.
 *
 * This is intentionally separate from SubjectThumbnail (which renders true
 * subject illustrations like a chalkboard or open book). TileEmblem is
 * for non-subject features (Adaptive Assessment, AI Hub, GL Assessment,
 * etc.) where a literal "math chalkboard" would feel out of place.
 */

import { motion } from 'framer-motion';

const PATTERNS = {
  // soft scatter of dots — generic accent
  dots: (
    <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full opacity-50" aria-hidden="true">
      <circle cx="9" cy="11" r="1.5" fill="currentColor" />
      <circle cx="45" cy="9" r="1.2" fill="currentColor" />
      <circle cx="51" cy="46" r="1.6" fill="currentColor" />
      <circle cx="6" cy="48" r="1.3" fill="currentColor" />
      <circle cx="34" cy="50" r="1" fill="currentColor" />
    </svg>
  ),
  // sparkles — for AI / discovery tiles
  sparkles: (
    <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full opacity-60" aria-hidden="true">
      <path d="M9 12 L11 18 L17 20 L11 22 L9 28 L7 22 L1 20 L7 18 Z" fill="currentColor" transform="scale(0.55) translate(8,8)" />
      <path d="M44 8 L46 14 L52 16 L46 18 L44 24 L42 18 L36 16 L42 14 Z" fill="currentColor" transform="scale(0.45) translate(60,4)" />
      <path d="M40 36 L42 42 L48 44 L42 46 L40 52 L38 46 L32 44 L38 42 Z" fill="currentColor" transform="scale(0.5) translate(38,30)" />
    </svg>
  ),
  // ascending bars — for practice / progression tiles
  bars: (
    <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full opacity-40" aria-hidden="true">
      <rect x="4" y="44" width="4" height="6" rx="1" fill="currentColor" />
      <rect x="12" y="40" width="4" height="10" rx="1" fill="currentColor" />
      <rect x="20" y="34" width="4" height="16" rx="1" fill="currentColor" />
      <rect x="44" y="6" width="4" height="6" rx="1" fill="currentColor" />
      <rect x="50" y="12" width="3" height="4" rx="1" fill="currentColor" />
    </svg>
  ),
  // ribbon waves — for resources / library tiles
  waves: (
    <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full opacity-50" aria-hidden="true">
      <path d="M-2 14 Q 14 6 28 14 T 58 14" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M-2 24 Q 14 16 28 24 T 58 24" stroke="currentColor" strokeWidth="1.2" fill="none" opacity="0.7" />
    </svg>
  ),
  // small target rings — for adaptive / diagnostic
  rings: (
    <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full opacity-45" aria-hidden="true">
      <circle cx="46" cy="10" r="6" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="46" cy="10" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="8" cy="48" r="5" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
};

/**
 * @param {React.ComponentType} icon  - Lucide icon component
 * @param {string} accent             - "from-violet-500 to-indigo-600" etc.
 * @param {string} pattern            - 'dots' | 'sparkles' | 'bars' | 'waves' | 'rings'
 * @param {'sm'|'md'|'lg'} size       - default 'md' = 56px
 */
const TileEmblem = ({ icon: Icon, accent = 'from-violet-500 to-indigo-600', pattern = 'dots', size = 'md', className = '' }) => {
  const dim = size === 'sm' ? 'h-11 w-11' : size === 'lg' ? 'h-16 w-16' : 'h-14 w-14';
  const iconSize = size === 'sm' ? 'h-5 w-5' : size === 'lg' ? 'h-7 w-7' : 'h-6 w-6';
  const PatternSvg = PATTERNS[pattern] || PATTERNS.dots;

  return (
    <motion.div
      initial={{ scale: 0.94, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      className={`relative ${dim} rounded-xl bg-gradient-to-br ${accent} shadow-sm overflow-hidden shrink-0 ${className}`}
    >
      {/* Ornament — currentColor inherits white so it sits on the gradient. */}
      <div className="absolute inset-0 text-white">{PatternSvg}</div>
      {/* Icon */}
      <div className={`relative h-full w-full flex items-center justify-center text-white`}>
        {Icon ? <Icon className={iconSize} /> : null}
      </div>
    </motion.div>
  );
};

export default TileEmblem;
